/* ==========================================================
   LIME LABS COMMERCE LAYER
   Component: Mexico Campaign Announcement
   Project: La Troupe
   File: assets/ll-announcement-mexico.js
   Version: 1.1.0
   Status: LLCL-QA

   Owns:
   - campaign phase: prelaunch / live / ended
   - countdown
   - Theme Editor preview phase
   - announcement height contract for header
   - Theme Editor lifecycle

   Performance guard:
   - geometry reads are coalesced to one requestAnimationFrame
     so resize/ResizeObserver bursts do not repeat synchronous layout.

   No scroll hijacking. No popup. No dependencies.
   ========================================================== */

(() => {
  'use strict';

  const VERSION = '1.1.0';
  const MANAGER_KEY = 'LLAnnouncementMexicoManager';
  const ROOT_SELECTOR = '[data-ll-announcement-mexico]';
  const FORCED_PREVIEW_MS = 8 * 60 * 60 * 1000;

  const previousManager = window[MANAGER_KEY];

  if (previousManager && previousManager.version === VERSION) {
    previousManager.scan(document);
    return;
  }

  if (previousManager && typeof previousManager.destroy === 'function') {
    previousManager.destroy();
  }

  const pad = (value) => String(Math.max(0, value)).padStart(2, '0');

  const parseDate = (value) => {
    const parsed = Date.parse(value || '');
    return Number.isFinite(parsed) ? parsed : null;
  };

  let headerSyncFrame = 0;

  const measureAndWriteHeaderContract = () => {
    const wrappers = Array.from(
      document.querySelectorAll('.shopify-section--announcement-bar')
    );

    let totalHeight = 0;
    let anySticky = false;

    wrappers.forEach((wrapper) => {
      const style = window.getComputedStyle(wrapper);
      if (style.display === 'none' || style.visibility === 'hidden') return;

      const rect = wrapper.getBoundingClientRect();
      if (rect.height > 0.5) totalHeight += rect.height;

      const root = wrapper.querySelector('[data-ll-announcement-mexico], [data-ll-announcement]');
      if (root?.dataset.sticky === 'true') anySticky = true;
    });

    document.documentElement.style.setProperty(
      '--announcement-bar-height',
      `${Math.round(totalHeight)}px`
    );

    document.documentElement.style.setProperty(
      '--announcement-bar-is-sticky',
      anySticky ? '1' : '0'
    );
  };

  const syncHeaderContract = () => {
    if (headerSyncFrame) return;

    headerSyncFrame = window.requestAnimationFrame(() => {
      headerSyncFrame = 0;
      measureAndWriteHeaderContract();
    });
  };

  class LLAnnouncementMexicoController {
    constructor(root) {
      this.root = root;
      this.timer = null;
      this.resizeObserver = null;
      this.forcedPreviewTarget = null;
      this.phase = null;
      this.destroyed = false;

      this.refresh = this.refresh.bind(this);
      this.tick = this.tick.bind(this);

      this.init();
    }

    init() {
      this.root.dataset.llAnnouncementMexicoBound = 'true';
      this.start();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(syncHeaderContract);
        this.resizeObserver.observe(this.root);
      }

      syncHeaderContract();
    }

    getPreviewPhase() {
      if (this.root.dataset.designMode !== 'true') return 'auto';
      return this.root.dataset.previewPhase || 'auto';
    }

    getLaunchTarget() {
      if (this.getPreviewPhase() === 'prelaunch') {
        if (!this.forcedPreviewTarget) {
          this.forcedPreviewTarget = Date.now() + FORCED_PREVIEW_MS;
        }
        return this.forcedPreviewTarget;
      }

      return parseDate(this.root.dataset.launchAt);
    }

    getEndTarget() {
      return parseDate(this.root.dataset.endAt);
    }

    start() {
      window.clearInterval(this.timer);
      this.tick();

      if (this.phase === 'prelaunch') {
        this.timer = window.setInterval(this.tick, 1000);
      } else if (this.phase === 'live') {
        this.timer = window.setInterval(this.tick, 15000);
      }
    }

    tick() {
      if (this.destroyed) return;

      const preview = this.getPreviewPhase();

      if (preview === 'live') {
        this.setPhase('live');
        return;
      }

      const now = Date.now();
      const launch = this.getLaunchTarget();
      const end = this.getEndTarget();

      if (preview === 'auto' && end && now >= end) {
        this.setPhase('ended');
        return;
      }

      if (!launch || now >= launch) {
        this.setPhase('live');
        return;
      }

      this.setPhase('prelaunch');
      this.renderCountdown(launch - now);
    }

    renderCountdown(remaining) {
      const totalSeconds = Math.max(0, Math.floor(remaining / 1000));
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const compact = days > 0
        ? `${pad(days)}D ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
        : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

      this.root.querySelectorAll('[data-ll-announcement-countdown]').forEach((node) => {
        node.textContent = compact;
      });

      this.root.setAttribute(
        'aria-label',
        `México nos habita. La promoción comienza en ${days} días, ${hours} horas, ${minutes} minutos y ${seconds} segundos.`
      );
    }

    setPhase(phase) {
      if (!['prelaunch', 'live', 'ended'].includes(phase)) return;

      const changed = this.phase !== phase;
      this.phase = phase;

      this.root.classList.toggle('is-prelaunch', phase === 'prelaunch');
      this.root.classList.toggle('is-live', phase === 'live');
      this.root.classList.toggle('is-ended', phase === 'ended');
      this.root.dataset.campaignPhase = phase;
      this.root.hidden = phase === 'ended';

      if (changed) {
        syncHeaderContract();

        this.root.dispatchEvent(
          new CustomEvent('ll:announcement-mexico-phase', {
            bubbles: true,
            detail: {
              phase,
              campaignId: this.root.dataset.campaignId || ''
            }
          })
        );

        if (phase !== 'prelaunch') {
          window.clearInterval(this.timer);
          this.timer = phase === 'live'
            ? window.setInterval(this.tick, 15000)
            : null;
        }
      }
    }

    refresh() {
      if (this.destroyed) return;
      this.forcedPreviewTarget = null;
      this.start();
      syncHeaderContract();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      window.clearInterval(this.timer);
      this.resizeObserver?.disconnect();
      this.root.dataset.llAnnouncementMexicoBound = 'false';
    }
  }

  const controllers = new Map();

  const prune = () => {
    controllers.forEach((controller, root) => {
      if (root.isConnected) return;
      controller.destroy();
      controllers.delete(root);
    });
  };

  const initialize = (root) => {
    if (!root || controllers.has(root)) return;
    controllers.set(root, new LLAnnouncementMexicoController(root));
  };

  const scan = (context = document) => {
    prune();
    if (context.matches?.(ROOT_SELECTOR)) initialize(context);
    context.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  };

  const destroyWithin = (target) => {
    controllers.forEach((controller, root) => {
      if (root === target || target?.contains?.(root)) {
        controller.destroy();
        controllers.delete(root);
      }
    });
    syncHeaderContract();
  };

  const refreshWithin = (target) => {
    scan(target);
    controllers.forEach((controller, root) => {
      if (root === target || target?.contains?.(root)) controller.refresh();
    });
  };

  const onSectionLoad = (event) => refreshWithin(event.target);
  const onSectionUnload = (event) => destroyWithin(event.target);
  const onSectionSelect = (event) => refreshWithin(event.target);
  const onSectionReorder = () => {
    controllers.forEach((controller) => controller.refresh());
    syncHeaderContract();
  };

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:select', onSectionSelect);
  document.addEventListener('shopify:section:reorder', onSectionReorder);
  window.addEventListener('resize', syncHeaderContract, { passive: true });

  const manager = {
    version: VERSION,
    scan,
    refresh() {
      scan(document);
      controllers.forEach((controller) => controller.refresh());
    },
    status() {
      const rows = [];
      controllers.forEach((controller) => {
        rows.push({
          version: VERSION,
          campaign: controller.root.dataset.campaignId || '',
          phase: controller.phase,
          launchAt: controller.root.dataset.launchAt || '',
          endAt: controller.root.dataset.endAt || '',
          previewPhase: controller.root.dataset.previewPhase || 'auto'
        });
      });
      console.table(rows);
      return rows;
    },
    destroy() {
      controllers.forEach((controller) => controller.destroy());
      controllers.clear();
      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      document.removeEventListener('shopify:section:select', onSectionSelect);
      document.removeEventListener('shopify:section:reorder', onSectionReorder);
      window.removeEventListener('resize', syncHeaderContract);
      if (headerSyncFrame) {
        cancelAnimationFrame(headerSyncFrame);
        headerSyncFrame = 0;
      }
      measureAndWriteHeaderContract();
    }
  };

  window[MANAGER_KEY] = manager;
  window.LLAnnouncementMexico = manager;

  const start = () => scan(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
